import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { AxiosInstance } from 'axios'
import { publicApiClient, adminApiClient } from '../api/client'
import { Image } from '../types/image'
import { PaginatedResponse } from '../types/api'

export const useImages = (
  apiClient: AxiosInstance = publicApiClient,
  params?: { 
    albumId?: string; 
    limit?: number;
    sortBy?: 'date';
    sortOrder?: 'asc' | 'desc';
    shareToken?: string;
  }
) => {
  // Include API client type in query key to separate public/admin caches
  const clientType = apiClient === publicApiClient ? 'public' : 'admin'
  
  return useInfiniteQuery({
    queryKey: ['images', clientType, params],
    queryFn: async ({ pageParam }) => {
      const headers: Record<string, string> = {}
      if (params?.shareToken) {
        headers['X-Share-Token'] = params.shareToken
      }

      const { data } = await apiClient.get('/images', {
        params: {
          limit: params?.limit || 50,
          cursor: pageParam,
          albumId: params?.albumId,
          sortBy: params?.sortBy,
          sortOrder: params?.sortOrder,
        },
        headers,
      })
      return data.data as PaginatedResponse<Image>
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })
}

export const useImage = (id: string, apiClient: AxiosInstance = publicApiClient) => {
  // Include API client type in query key to separate public/admin caches
  const clientType = apiClient === publicApiClient ? 'public' : 'admin'
  
  return useQuery({
    queryKey: ['image', clientType, id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/images/${id}`)
      return data.data as Image
    },
    enabled: !!id,
  })
}

export const useUpdateImage = (apiClient: AxiosInstance) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Image> }) => {
      await apiClient.patch(`/images/${id}`, data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['image', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['images'] })
      // Moving an image changes album counts and date ranges, so refresh albums too.
      if (variables.data.albumId) {
        queryClient.invalidateQueries({ queryKey: ['albums'] })
      }
    },
  })
}

export const useDeleteImage = (apiClient: AxiosInstance) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/images/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['images'] })
    },
  })
}

export const useRefreshExif = (apiClient: AxiosInstance = adminApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (imageId: string) => {
      const { data } = await apiClient.post(`/images/${imageId}/refresh-exif`)
      return data.data
    },
    onSuccess: (_, imageId) => {
      queryClient.invalidateQueries({ queryKey: ['image', imageId] })
      queryClient.invalidateQueries({ queryKey: ['images'] })
    },
  })
}

export const useRefreshAlbumImages = (apiClient: AxiosInstance = adminApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (albumId: string) => {
      const { data } = await apiClient.post(`/albums/${albumId}/refresh-all`)
      return data.data as { queued: number; errors: number }
    },
    onSuccess: (_, albumId) => {
      // Invalidate both admin image queries and the specific album
      queryClient.invalidateQueries({ queryKey: ['images', 'admin'] })
      queryClient.invalidateQueries({ queryKey: ['album', 'admin', albumId] })
    },
  })
}
